import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { AutosaveFormEnumMultiselect } from "@/components/companies/shared/autosave-form/AutosaveFormEnumMultiselect";
import { AutosaveFormEnumSelect } from "@/components/companies/shared/autosave-form/AutosaveFormEnumSelect";
import { AutosaveFormLinkInput } from "@/components/companies/shared/autosave-form/AutosaveFormLinkInput";
import { AutosaveFormRichTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormRichTextInput";
import { AutosaveFormTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormTextInput";
import { IntakeBoolean } from "./IntakeBoolean";
import { IntakeForm } from "./IntakeForm";
import { Column, ErrorText, Row } from "@/components";
import { Label } from "@/components/ui/label";

import { useUpdateSetStakeholder } from "@/hooks/updateSets/useUpdateSetStakeholder";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import {
  communicationFrequencyOptions,
  communicationModeOptions,
  communicationModeToLabel,
  stakeholderRoleOptions,
  stakeholderRoleToLabel,
  stakeholderValuePerceptionOptions,
  stakeholderValuePerceptionToLabel,
} from "@/lib/constants";
import {
  CommunicationFrequency,
  CommunicationMode,
  Stakeholder,
  StakeholderRole,
  StakeholderValuePerception,
} from "@/types";

const stakeholderBasicsSchema = z.object({
  // Basics
  firstName: z.string().optional().nullable(),
  lastName: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  linkedinUrl: z
    .string()
    .url("Please enter a valid URL")
    .optional()
    .nullable()
    .or(z.literal("")),
  role: z.array(z.nativeEnum(StakeholderRole)).optional().nullable(),
  personalityAndNotes: z.string().optional().nullable(),

  // Communication Preferences
  communicationMode: z
    .array(z.nativeEnum(CommunicationMode))
    .optional()
    .nullable(),
  primaryContact: z.boolean().optional(),
  ccOnEmails: z.boolean().optional(),
  communicationStyle: z.string().optional().nullable(),
  communicationFrequency: z
    .nativeEnum(CommunicationFrequency)
    .optional()
    .nullable(),
  communicationDetails: z.string().optional().nullable(),

  // Value Perception
  valuePerception: z.array(z.nativeEnum(StakeholderValuePerception)).optional(),
  valuedCandidateQualities: z.string().optional().nullable(),
  commonRejectionReasons: z.string().optional().nullable(),

  // View on External Partners
  viewOnExternalPartners: z.string().optional().nullable(),
});

export const StakeholderForm = ({
  stakeholder,
}: {
  stakeholder: Stakeholder;
}) => {
  const { mutateAsync: updateStakeholderAsync, isError } =
    useUpdateSetStakeholder();

  const form = useForm<z.infer<typeof stakeholderBasicsSchema>>({
    resolver: zodResolver(stakeholderBasicsSchema),
    defaultValues: {
      ...stakeholder,
      communicationMode: stakeholder.communicationMode ?? [],
      valuePerception: stakeholder.valuePerception ?? [],
    },
  });

  const primaryContact = form.watch("primaryContact");
  const ccOnEmails = form.watch("ccOnEmails");

  const handleSave = async (data: z.infer<typeof stakeholderBasicsSchema>) => {
    mixpanelUtils.trackFormSubmission("Stakeholder form", MIXPANEL_SCREENS.COMPANY);
    await updateStakeholderAsync({
      id: stakeholder.id,
      updateStakeholderRequest: data,
    });
  };

  const saveOnBlur = () => {
    return form.handleSubmit(handleSave)();
  };

  return (
    <IntakeForm form={form} onSave={() => void saveOnBlur()}>
      <Column className="gap-12">
        <Column className="gap-2">
          <Label className="text-md font-semibold text-slate-700">Basics</Label>

          <Column className="gap-4">
            <Row className="gap-4">
              <AutosaveFormTextInput
                className="flex-1"
                fieldName="firstName"
                form={form}
                label="First Name"
                onBlur={saveOnBlur}
                placeholder="e.g., Moo"
              />
              <AutosaveFormTextInput
                className="flex-1"
                fieldName="lastName"
                form={form}
                label="Last Name"
                onBlur={saveOnBlur}
                placeholder="e.g., Deng"
              />
              <AutosaveFormTextInput
                className="flex-1"
                fieldName="title"
                form={form}
                label="Title"
                onBlur={saveOnBlur}
                placeholder="e.g., Hiring Manager, VP of Operations"
              />
            </Row>

            <Row className="gap-4">
              <AutosaveFormTextInput
                className="min-w-0 flex-1"
                fieldName="phone"
                form={form}
                label="Phone"
                onBlur={saveOnBlur}
                placeholder="e.g., (555) 123-4567"
              />
              <AutosaveFormTextInput
                className="min-w-0 flex-1"
                fieldName="email"
                form={form}
                label="Email"
                onBlur={saveOnBlur}
                placeholder="e.g., moodeng@laborup.com"
              />
              <AutosaveFormLinkInput
                className="flex-1"
                fieldName="linkedinUrl"
                form={form}
                label="LinkedIn URL"
                onBlur={saveOnBlur}
                placeholder="e.g., https://www.linkedin.com/in/moo-deng-83671b341/"
              />
            </Row>

            <AutosaveFormEnumMultiselect
              enumToLabel={stakeholderRoleToLabel}
              fieldName="role"
              form={form}
              label="Role"
              onSelect={saveOnBlur}
              options={stakeholderRoleOptions}
            />

            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="personalityAndNotes"
              form={form}
              infoTooltip={
                "Any personal interests, hobbies, or conversation starters?\nAnything to be mindful of when interacting with them?"
              }
              label="Personality and Notes"
              onBlur={saveOnBlur}
            />
          </Column>
        </Column>

        <Column className="gap-2">
          <Label className="text-md font-semibold text-slate-700">
            Communication Preferences
          </Label>

          <Column className="gap-4">
            <div className="grid grid-cols-3 gap-4">
              <IntakeBoolean
                checked={primaryContact}
                label="Primary Contact"
                onToggle={() => {
                  form.setValue("primaryContact", !primaryContact);
                  void saveOnBlur();
                }}
              />
              <IntakeBoolean
                checked={ccOnEmails}
                label="CC'd on Emails"
                onToggle={() => {
                  form.setValue("ccOnEmails", !ccOnEmails);
                  void saveOnBlur();
                }}
              />
              <div />
              <AutosaveFormEnumMultiselect
                enumToLabel={communicationModeToLabel}
                fieldName="communicationMode"
                form={form}
                label="Preferred Mode of Communication"
                onSelect={saveOnBlur}
                options={communicationModeOptions}
              />
              <AutosaveFormEnumSelect
                fieldName="communicationFrequency"
                infoTooltip="Assuming there are live requisitions"
                form={form}
                label="Preferred Communication Frequency"
                onSelect={saveOnBlur}
                options={communicationFrequencyOptions}
              />
              <AutosaveFormTextInput
                fieldName="communicationStyle"
                form={form}
                infoHint={
                  "Brief\nDirect\nFriendly\nHumorous\nProfessional\nThorough"
                }
                label="Preferred Communication Style"
                onBlur={saveOnBlur}
                placeholder="e.g., Brief, Direct, Professional"
              />
            </div>

            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="communicationDetails"
              form={form}
              infoTooltip={
                "What's their communication style — direct, reserved, chatty?\nHow do they prefer to build rapport — small talk, straight to business?"
              }
              label="Communication Details"
              onBlur={saveOnBlur}
            />
          </Column>
        </Column>

        <Column className="gap-2">
          <Label className="text-md font-semibold text-slate-700">
            Value Perception
          </Label>

          <Column className="gap-4">
            <AutosaveFormEnumMultiselect
              enumToLabel={stakeholderValuePerceptionToLabel}
              fieldName="valuePerception"
              form={form}
              label="How do they perceive value (select max 2)"
              maxSelections={2}
              onSelect={saveOnBlur}
              options={stakeholderValuePerceptionOptions}
            />

            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="valuedCandidateQualities"
              form={form}
              infoTooltip={
                "What do they care about most when evaluating candidates?\nWho on your team has worked out best?\nWhat made them successful?\nWhat qualities made them stand out?"
              }
              label="Valued Candidate Qualities"
              onBlur={saveOnBlur}
            />

            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="commonRejectionReasons"
              form={form}
              infoTooltip={
                "What's the most common reason you pass on a candidate?\nWhat have past hires been missing?"
              }
              label="Common Rejection Reasons"
              onBlur={saveOnBlur}
            />
          </Column>
        </Column>

        <Column className="gap-2">
          <Label className="text-md font-semibold text-slate-700">
            View on External Partners
          </Label>

          <Column className="gap-4">
            <AutosaveFormRichTextInput
              fieldName="viewOnExternalPartners"
              form={form}
              infoTooltip={`Are they optimistic or pessimistic about working with Laborup and any other staffing agencies?\nHave they had good or bad experiences with staffing agencies in the past?\nAre they skeptical about the value Laborup brings to the table?`}
              label="How do they view external partners?"
              onBlur={saveOnBlur}
            />
          </Column>
        </Column>

        {isError && (
          <ErrorText errorMessage="An error occurred while saving. Please try again." />
        )}
      </Column>
    </IntakeForm>
  );
};
